import deepmerge from 'deepmerge';
import { BirthDateValidation } from '../components/atoms/DateInput/getBirthDateValidator';
import { PhoneNumberValidation } from '../components/atoms/PhoneInput/getPhoneNumberValidator';
import { NotificationLevel } from '../components/molecules/Notifications';
import defaultConfig from '../defaults/config.json';
import defaultTranslations from '../defaults/translations.json';
import { Coordinates } from '../types/collectionPoints';
import { AppQualifier } from './appQualifier';
import createApiError, { ApiError } from './createApiError';
import { Validation } from './getValidator';
import { Step } from './routing';
import { cofeNextAbTestLocalStorageHost } from './setCofeNextAbTest';
import { wait } from './wait';

/**
 * Locale with underscore (e.g. de_DE, en_US, nl_BE)
 */
export type Locale = string;

/**
 * ISO 4217 currency code (e.g. EUR, USD, SAR)
 */
export type Currency = string;

/**
 * ISO 3166-1 alpha-2 country code (e.g. DE, FR, BE)
 */
export type Country = string;

export type SubstitutionOptions = { input: string; output: string };

export type BasicFeature = { enabled: boolean };

export type NotImplemented = Record<string, never>;

export type Provider = { key: string; color?: string };

export type OptionDescriptions = { short: boolean; toolTip: boolean };

export type AddressForm = {
  specialComponent?: boolean;
  title: BasicFeature;
  googleMapsAutocomplete: BasicFeature;
  streetHouseNumber: {
    separateFields: boolean;
    houseNumber: { position: 'first' | 'second' } & Validation;
    street: Validation;
  } & Validation;
  additional: BasicFeature & Validation;
  zipCodeCity: {
    zipCode: BasicFeature & { position: 'first' | 'second' } & Validation & {
        allowList?: boolean;
        blockList?: boolean;
        completion: BasicFeature & { disableSpecialComponentSuggestion?: boolean };
        formatOnBlur?: SubstitutionOptions;
      };
    city: Validation & { completion: BasicFeature };
  };
  state: BasicFeature & Validation & { select: BasicFeature & { blockList?: string[] } };
};

export type ConfigNotification = {
  textTranslationKey: string;
  startDateTime?: string;
  endDateTime?: string;
  steps?: Step[];
  type?: NotificationLevel;
};

type Link = { key: string; link: string };

export type ConditionField =
  | 'carrierGroupKey'
  | 'shippingOptionKey'
  | 'collectionPointType'
  | 'paymentOptionKey'
  | 'shippingCountry'
  | 'billingCountry'
  | 'minBasketTotal';
export type Condition = Partial<Record<ConditionField, string[]> & { minBasketTotal?: number }>;
export type Conditions = { enabled?: Condition; required?: Condition };
export type TextsConfig = {
  name: string;
  i18nKeys: { label: string; headline?: string };
  conditions?: Conditions;
};
export type CheckboxConfig = TextsConfig & { required?: boolean; initialValue?: boolean };
export type DynamicFields = {
  taxId?: BasicFeature & Validation & { conditions?: Conditions };
  birthDate?: BasicFeature & BirthDateValidation & { conditions?: Conditions };
  phoneNumber?: BasicFeature & PhoneNumberValidation & { conditions?: Conditions };
  logisticsPhoneNumber?: BasicFeature & PhoneNumberValidation & { conditions?: Conditions };
  carrierCustomerId?: BasicFeature & Validation & { conditions?: Conditions };
  checkboxes?: CheckboxConfig[];
  texts?: TextsConfig[];
};
export type DynamicFieldAreaKey =
  | 'register'
  | 'guest'
  | 'shippingAddress'
  | 'collectionPoint'
  | 'billingAddress'
  | 'paymentOptions'
  | 'payment'
  | 'paymentBelowMobileBasket'
  | 'review';

export type Session = {
  jwtSecret: string;
  token?: string;
  accessToken?: string;
  correlationId?: string;
};

export type AbTest = {
  experimentId: string;
  percentage: number;
  conditions?: { customerGroup?: string[] };
};

export type Config = AppQualifier & {
  header: {
    desktopHeight: number;
    mobileHeight: number;
  };
  statePollInterval: number;
  paymentConfirmationPollTimeout: number;
  features: {
    breadcrumbs: BasicFeature;
    expressCheckout: {
      payPal: BasicFeature & NotImplemented;
      applePay: BasicFeature & NotImplemented;
      amazonPay: BasicFeature & NotImplemented;
    };
    customerNumberSignIn: BasicFeature & Validation;
    passwordVisibilityToggle: BasicFeature;
    secondEmailField: BasicFeature;
    thirdPartySignIn: BasicFeature & {
      disableFirstPartySignIn: boolean;
      providers: Provider[];
    };
    guestCheckout: BasicFeature;
    fullCountryNames: BasicFeature;
    neutralGender: BasicFeature;
    notSpecifiedGender: BasicFeature;
    addressBook: BasicFeature;
    discountCodes: {
      position: 'basket' | 'payment';
    };
    vouchers: BasicFeature & Validation & { disableInitialVoucherNotification: boolean };
    giftCards: BasicFeature & Validation & { pin: BasicFeature & Validation };
    loyaltyProgram: BasicFeature &
      Validation & {
        type: string;
        disableRemoval: boolean;
        icon: BasicFeature;
        toolTip: BasicFeature;
        formatOnBlur?: SubstitutionOptions;
        redeemablePoints: BasicFeature;
        registration: BasicFeature & { disableForGuest: boolean };
      };
    lowestPriorPrice: BasicFeature;
    shippingCostToolTip: BasicFeature;
    dutiesAndTaxes: BasicFeature;
    sentry: BasicFeature;
    datadog: BasicFeature & { sessionSampleRate: number; forwardErrorsToLogs: boolean };
    appDeepLinkRewrites: BasicFeature;
    review: {
      showAlways: boolean;
    };
  };
  currency: Currency;
  formats: {
    date: string;
    currency: 'symbol' | 'code' | 'name' | 'narrowSymbol';
    hideCurrencyDecimals: boolean;
  };
  names: {
    firstName: Validation;
    lastName: { position: 'first' | 'second' } & Validation;
  };
  emailPassword: {
    email: Pick<Validation, 'layeredValidation'>;
    password: Validation;
  };
  tracking: {
    sendPlaintextEmail: boolean;
    sendDataLayerEvents: boolean;
    sendFormFieldErrors: boolean;
  };
  basket: {
    position: 'bottom' | 'top';
    showSoldOutItems: boolean;
    hideBrands: boolean;
    hideOnAuthStep: boolean;
    hideItemRemovalButton: boolean;
    disableQuantityChange: boolean;
    hasDisabledLinks: boolean;
    imageParameters: string | null;
    showNetPrices?: boolean;
    showCollapsiblePromotion: boolean;
    showMerchantInformation: boolean;
    replaceFreeItemPriceWithText: boolean;
  };
  deliveryOptions: {
    preferred: 'homeDelivery' | 'collectionPoint' | 'storePickup';
    suggestCollectionPoint: BasicFeature;
    forwardToCollectionPoint: BasicFeature;
  };
  carrierOptions: {
    position: 'shippingTop' | 'shippingBottom' | 'payment';
    suggestCollectionPoint: BasicFeature;
    isCollapsible: BasicFeature;
  };
  collectionPoints: {
    defaultMaximumDistance: number;
    maximumDistanceOptions: number[];
    googleMapsAutocomplete: BasicFeature;
    preferredView: 'map' | 'list';
  };
  shippingOptions: { descriptions: OptionDescriptions; hideDeliveryEstimation: boolean };
  paymentOptions: { descriptions: OptionDescriptions; hidePaymentIcons: boolean };
  addressForms: Record<Country, AddressForm>;
  dynamicFields: Partial<Record<DynamicFieldAreaKey, DynamicFields>>;
  links: Link[];
  notifications: ConfigNotification[];
  customizedAssets: string[];
  externalAssets: Record<string, string>;
  styles: Record<string, string>;
  isProduction: boolean;
  availableCountries: {
    shipping: { iso2Code: Country; centerCoordinates: Coordinates }[];
    billing?: { iso2Code: Country }[];
  };
  session: Session;
  translations: Record<string, any>;
  urls: { passwordResetConfirmation: string };
  abTests: AbTest[];
  version: string;
};

const fullPath = import.meta.url;
const CDN_HOST = new URL(fullPath).origin;
export const DISTRIBUTION_URL = import.meta.env.DEV ? CDN_HOST : fullPath.substring(0, fullPath.lastIndexOf('/'));

export let config: Config;

export const getCdnShopUrl = ({ environment, shop }: Pick<AppQualifier, 'environment' | 'shop'>) =>
  environment ? `${CDN_HOST}/shops/${environment}/${shop}` : `${CDN_HOST}/shops/${shop}`;

export const getAssetUrl = (path: string) => {
  if (path in config.externalAssets) {
    return config.externalAssets[path];
  } else if (config.customizedAssets.includes(path)) {
    return `${getCdnShopUrl(config)}/assets/${path}`;
  } else {
    return `${DISTRIBUTION_URL}/assets/${path}`;
  }
};

export const setProvisionalConfig = async (
  appQualifier: AppQualifier,
  session?: Pick<Session, 'token' | 'accessToken' | 'correlationId'>
) => {
  const calculatedApiHost = localStorage.getItem(cofeNextAbTestLocalStorageHost) ?? appQualifier.apiHost;
  config = {
    ...appQualifier,
    ...defaultConfig,
    session: session ?? {},
    translations: defaultTranslations,
    apiHost: calculatedApiHost,
    useCheckoutNext: calculatedApiHost.includes('/next')
  } as unknown as Config;
};

const validateAddressFormConfig = (_config: Config) => {
  const allCountries = [
    ..._config.availableCountries.shipping.map(({ iso2Code }) => iso2Code),
    ...(_config.availableCountries.billing?.map(({ iso2Code }) => iso2Code) ?? [_config.country])
  ];
  const uniqueCountries = [...new Set(allCountries)];
  uniqueCountries.forEach(country => {
    if (!(country in _config.addressForms)) {
      console.error(`No address form config found for country ${country}`);
    }
  });
};

const overwriteMerge = (_: unknown, sourceArray: unknown[]) => sourceArray;

const loadCdnConfig = async () => {
  const cdnConfigUrl = `${getCdnShopUrl(config)}/config.json`;
  const response = await fetch(cdnConfigUrl);
  if (response.status >= 400) {
    console.warn(`Could not load shop config (${cdnConfigUrl}). Using default config only.`);
    return {};
  } else {
    return response.json();
  }
};

const callApiForBackendConfig = async () => {
  const headers: Record<string, string> = {
    'X-Shop-ID': config.appId.toString()
  };
  if (config.session.token) {
    headers['X-Session'] = config.session.token;
  }
  if (config.session.accessToken) {
    headers.Authorization = `Bearer ${config.session.accessToken}`;
  }
  if (config.session.correlationId) {
    headers['X-Correlation-ID'] = config.session.correlationId;
  }
  const response = await fetch(`${config.apiHost}/api/co/v3/state/config`, { credentials: 'include', headers });
  if (response.status === 401) {
    window.location.reload();
    throw new ApiError('401 (Unauthorized) received - Reloading page...', 401);
  } else if (response.status >= 400) {
    throw await createApiError(response);
  } else {
    return response.json();
  }
};

let shouldRetryConfig = true;
const loadBackendConfig: () => Promise<any> = async () => {
  if (window.ayCheckout?.initialBackendConfig) {
    return window.ayCheckout.initialBackendConfig;
  }
  try {
    return await callApiForBackendConfig();
  } catch (e: any) {
    e.endpoint = 'GET /co/api/v3/state/config';
    if (shouldRetryConfig) {
      shouldRetryConfig = false;
      await wait(35);
      return loadBackendConfig();
    }
    throw e;
  }
};

const mergeAddressFormConfigs = ({
  countryOverwrites,
  ...defaultAddressFormConfig
}: AddressForm & { countryOverwrites: Record<Country, AddressForm> }) =>
  Object.fromEntries(
    Object.entries(countryOverwrites).map(([key, countryOverwrite]) => [
      key,
      deepmerge.all([defaultAddressFormConfig, countryOverwrite], { arrayMerge: overwriteMerge })
    ])
  ) as Record<Country, AddressForm>;

const extractFormRules = (overwrites?: Record<Country, any>) => {
  const defaultValueName = { formRules: {}, rest: {} };
  if (!overwrites) {
    return defaultValueName;
  }

  return Object.entries(overwrites).reduce((acum, [key, { addressForm, ...rest }]) => {
    if (addressForm) {
      acum.formRules[key] = addressForm;
    }
    if (rest) {
      acum.rest[key] = rest;
    }
    return acum;
  }, defaultValueName);
};

export const loadConfig = async () => {
  const [{ environment, ...backendConfig }, { localeOverwrites, countryOverwrites: cdnOverwrites, ...cdnConfig }] =
    await Promise.all([loadBackendConfig(), loadCdnConfig()]);

  // Split countryOverwrites from defaultConfig so form overwrites can be extracted
  const { countryOverwrites: defaultOverwrites, ...defaultRules } = defaultConfig;

  // Extract form rules from default country overwrites
  const { formRules: defaultFormOverwrites, rest: otherDefaultOverwrites } = extractFormRules(defaultOverwrites);
  // Extract form rules from cdn country overwrites
  const { formRules: cdnFormOverwrites, rest: otherCdnOverwrites } = extractFormRules(cdnOverwrites);

  const { addressForm, ...configWithoutAddressForms } = deepmerge.all<any>(
    [
      defaultRules, //                                                     Default - generic rules
      { addressForm: { countryOverwrites: defaultFormOverwrites } }, //    Default - all form overwrites
      otherDefaultOverwrites?.[config.country] ?? {}, //                   Default - selected country overwrites
      config, //                                                           appQualifier data + default translations
      backendConfig, //                                                    backend translations
      cdnConfig, //                                                        CDN - generic rules
      { addressForm: { countryOverwrites: cdnFormOverwrites } }, //        CDN - all form overwrites
      otherCdnOverwrites?.[config.country] ?? {}, //                       CDN - selected country overwrites
      localeOverwrites?.[config.locale] ?? {},
      { isProduction: environment === 'production' }
    ],
    { arrayMerge: overwriteMerge }
  );

  config = { ...configWithoutAddressForms, addressForms: mergeAddressFormConfigs(addressForm) };

  validateAddressFormConfig(config);
};
